import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TYPE } from '@/theme/typography';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme,

  Link,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
  KeyboardCapslock,
  HelpOutlineRounded,
  ArrowBack,
} from '@mui/icons-material';
import { authApi } from '@/api/endpoints/auth';
import { useAuthStore } from '@/stores/authStore';
import PageMeta from '@/components/PageMeta';
import AnoorehrLogo from '@/components/auth/AnoorehrLogo';
import { MARKETING_URL, SUPPORT_URL } from '@/config/urls';
import { EMAIL } from '@/config/contacts';
import { MARKETING_PILLARS } from '@/config/marketingPillars';

// ── Types ────────────────────────────────────────────────────
type LoginStep = 'credentials' | '2fa';

interface LockoutInfo {
  message: string;
  retryAfterSeconds: number;
}

// ── Demo accounts (dev only) — seeded by `python -m app.seed_test_users` ──
// Platform entries sit on top (most-used for QA), tenant roles below.
//
// SECURITY: These constants must be tree-shaken from production bundles so the
// shared QA password and demo email list never leak into shipped JS. Vite
// inlines `import.meta.env.DEV` as a literal `true`/`false` at build time, so
// the entire `if` branch is dead code in prod builds and gets eliminated by
// esbuild/Rollup. Do NOT refactor to a runtime expression (e.g. reading from
// `window.__DEV__`) — that defeats tree-shaking. Verified absent from
// `dist/assets/*.js` via grep after `npm run build`.
const DEMO_PASSWORD: string = import.meta.env.DEV ? 'Demo@2026Secure' : '';
const DEMO_ACCOUNTS: Array<{ label: string; email: string }> = import.meta.env.DEV
  ? [
      // Platform (every role in PLATFORM_ROLES)
      { label: '[Platform] Owner (full access)', email: 'platform@demo.anoorehr.com' },
      { label: '[Platform] Support', email: 'platform-support@demo.anoorehr.com' },
      { label: '[Platform] Finance', email: 'platform-finance@demo.anoorehr.com' },
      { label: '[Platform] Read-only', email: 'platform-readonly@demo.anoorehr.com' },
      { label: '[Platform] Marketing (content-only)', email: 'platform-marketing@demo.anoorehr.com' },
      // Tenant (Acme Demo) — full-access + read-only roles
      { label: '[Acme Demo] Group Administrator', email: 'admin@demo.anoorehr.com' },
      { label: '[Acme Demo] Company Administrator', email: 'company-admin@demo.anoorehr.com' },
      { label: '[Acme Demo] Staff (self-service)', email: 'staff@demo.anoorehr.com' },
      { label: '[Acme Demo] Advisor (read-only + audit)', email: 'advisor@demo.anoorehr.com' },
      { label: '[Acme Demo] Read-only (all modules)', email: 'viewer@demo.anoorehr.com' },
      // Tenant (Acme Demo) — department / professional roles (scoped views)
      { label: '[Acme · HR] HR Manager', email: 'hr@demo.anoorehr.com' },
      { label: '[Acme · Finance] Accountant', email: 'accountant@demo.anoorehr.com' },
      { label: '[Acme · Finance] Bookkeeper (data entry)', email: 'bookkeeper@demo.anoorehr.com' },
      { label: '[Acme · Finance] Tax Preparer', email: 'taxprep@demo.anoorehr.com' },
      { label: '[Acme · Payroll] Payroll Officer', email: 'payroll@demo.anoorehr.com' },
      { label: '[Acme · Advisory] Consultant (read-only + AI)', email: 'consultant@demo.anoorehr.com' },
      // Tenant (Vintage Demo) — second tenant for cross-tenant RLS QA
      { label: '[Vintage Demo] Group Administrator', email: 'vintage-admin@demo.anoorehr.com' },
      { label: '[Vintage Demo] Company Administrator', email: 'vintage-company-admin@demo.anoorehr.com' },
      { label: '[Vintage Demo] Staff', email: 'vintage-staff@demo.anoorehr.com' },
      { label: '[Vintage Demo] Advisor (read-only)', email: 'vintage-advisor@demo.anoorehr.com' },
      { label: '[Vintage Demo] Viewer', email: 'vintage-viewer@demo.anoorehr.com' },
    ]
  : [];

// ── Component ────────────────────────────────────────────────
export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const theme = useTheme();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [step, setStep] = useState<LoginStep>('credentials');
  const [recoveryMode, setRecoveryMode] = useState(false);

  // Error / validation state
  const [error, setError] = useState<string | undefined>(undefined);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string; totp?: string }>({});
  const [loading, setLoading] = useState(false);

  // Caps Lock detection
  const [capsLock, setCapsLock] = useState(false);

  // Forces password field remount when quick login is used
  const [pwKey, setPwKey] = useState(0);

  // Lockout countdown
  const [lockout, setLockout] = useState<LockoutInfo | null>(null);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);

  // 2FA partial user info (from first step)
  const [twoFaUser, setTwoFaUser] = useState<{ email: string; first_name: string } | null>(null);

  // Server-signalled demo gate — production must return false regardless of
  // build mode. See /auth/demo-enabled. `null` = probe in flight; we render as
  // "not available" until the server confirms, so a slow/failed probe errs on
  // the side of hiding the dropdown rather than leaking it.
  const [demoServerEnabled, setDemoServerEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    authApi
      .demoEnabled()
      .then((res) => {
        if (!cancelled) setDemoServerEnabled(res.data.enabled === true);
      })
      .catch(() => {
        if (!cancelled) setDemoServerEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Lockout countdown timer ──────────────────────────────
  useEffect(() => {
    if (lockoutRemaining <= 0) return;
    const timer = setInterval(() => {
      setLockoutRemaining((prev) => {
        if (prev <= 1) {
          setLockout(null);
          setError(undefined);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [lockoutRemaining]);

  // ── Caps Lock handler ────────────────────────────────────
  const handleKeyEvent = useCallback((e: React.KeyboardEvent) => {
    if (typeof e.getModifierState === 'function') {
      setCapsLock(e.getModifierState('CapsLock'));
    }
  }, []);

  // ── Validation ───────────────────────────────────────────
  const validate = (): boolean => {
    const errs: { email?: string; password?: string } = {};
    const trimmed = email.trim();
    if (!trimmed) errs.email = t('auth.validation_email_required', 'Email is required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed))
      errs.email = t('auth.validation_email_invalid', 'Enter a valid email address');
    if (!password) errs.password = t('auth.validation_password_required', 'Password is required');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Handle login (step 1: credentials) ───────────────────
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setLockout(null);
    if (!validate()) return;
    setLoading(true);

    try {
      const { data } = await authApi.login({
        email: email.trim().toLowerCase(),
        password,
      });

      // Check if 2FA is required
      if (data.requires_2fa) {
        setTwoFaUser(data.user as { email: string; first_name: string });
        setStep('2fa');
        setLoading(false);
        return;
      }

      // Normal login — complete
      completeLogin(data);
    } catch (err: unknown) {
      handleLoginError(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Handle 2FA verification (step 2) ─────────────────────
  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    const code = totpCode.trim();
    if (recoveryMode) {
      // Recovery codes are alphanumeric with optional dashes; we normalize
      // server-side so the UI just needs a non-empty value.
      if (code.replace(/[^A-Za-z0-9]/g, '').length < 8) {
        setFieldErrors({ totp: t('auth.validation_recovery_invalid', 'Enter your recovery code') });
        return;
      }
    } else if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setFieldErrors({ totp: t('auth.validation_totp_invalid', 'Enter a 6-digit code') });
      return;
    }
    setFieldErrors({});
    setLoading(true);

    try {
      const { data } = recoveryMode
        ? await authApi.loginWith2FARecovery(code)
        : await authApi.verify2FA(code);
      completeLogin(data);
    } catch (err: unknown) {
      handleLoginError(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Shared login completion ──────────────────────────────
   
  const completeLogin = (data: any) => {
    setUser(
      {
        id: data.user.id,
        email: data.user.email,
        first_name: data.user.first_name,
        last_name: data.user.last_name,
        language: data.user.language,
        must_change_password: Boolean(data.user.must_change_password),
        totp_enabled: Boolean(data.user.totp_enabled),
        phone: data.user.phone ?? undefined,
        preferences: data.user.preferences ?? {},
      },
      data.companies,
      data.default_company_id ?? undefined,
      data.platform_role ?? null,
    );
    // Identify for product analytics (no-op if user hasn't granted consent)
    import('@/lib/analytics').then(({ identifyUser }) =>
      identifyUser(data.user.id, {
        email: data.user.email,
        companyId: data.default_company_id ?? undefined,
      }),
    );
    if (data.user.must_change_password) {
      navigate('/profile', { state: { tab: 2, forcePasswordChange: true } });
    } else {
      // Use server-resolved homepage (respects user pref > company default > role default)
      const homepage = data.default_homepage || '/dashboard';
      // Platform admins receive an absolute URL pointing to console.anoorehr.com
      // — use a full-page navigation so the browser crosses the origin boundary
      // instead of react-router treating it as an in-app path.
      if (/^https?:\/\//i.test(homepage)) {
        window.location.href = homepage;
      } else {
        navigate(homepage);
      }
    }
  };

  // ── Shared error handler ─────────────────────────────────
  const handleLoginError = (err: unknown) => {
    const axiosErr = err as {
      response?: { data?: { detail?: string | { message?: string; retry_after_seconds?: number } }; status?: number };
      request?: unknown;
    };

    if (!axiosErr.response && axiosErr.request) {
      setError(t('auth.server_unreachable'));
      return;
    }

    if (axiosErr.response?.status === 423) {
      const detail = axiosErr.response?.data?.detail;
      if (typeof detail === 'object' && detail !== null) {
        const seconds = detail.retry_after_seconds ?? 0;
        setLockout({ message: detail.message ?? '', retryAfterSeconds: seconds });
        setLockoutRemaining(seconds);
        setError(detail.message);
      } else {
        setError(t('auth.account_locked'));
      }
      return;
    }

    if (axiosErr.response?.status === 401 && step === '2fa') {
      // Pre-auth token expired — send user back to credentials
      setStep('credentials');
      setTotpCode('');
      setError(t('auth.session_expired', 'Session expired. Please log in again.'));
      return;
    }

    const detail = axiosErr.response?.data?.detail;
    const message = typeof detail === 'string' ? detail : t('auth.invalid_credentials');
    setError(message);
  };

  // ── Back to credentials from 2FA ─────────────────────────
  const handleBack = () => {
    setStep('credentials');
    setTotpCode('');
    setError(undefined);
    setFieldErrors({});
  };

  // ── Format seconds as M:SS ───────────────────────────────
  const formatCountdown = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default', position: 'relative' }}>
      <PageMeta
        title="Sign in"
        description="Sign in to AnooreHR — the HR, payroll, and accounting platform for African SMEs."
        canonical="https://app.anoorehr.com/login"
        indexable
      />

      {/* Back to marketing site — top-left, always visible */}
      <Link
        href={MARKETING_URL}
        underline="none"
        sx={{
          position: 'absolute',
          top: { xs: 16, md: 24 },
          left: { xs: 16, md: 24 },
          zIndex: 2,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: 2,
          fontSize: TYPE.base,
          fontWeight: TYPE.medium,
          color: { xs: 'text.secondary', md: 'rgba(255,255,255,0.75)' },
          transition: 'all 0.15s ease',
          '&:hover': {
            color: { xs: 'primary.main', md: 'common.white' },
            bgcolor: { xs: alpha(theme.palette.primary.main, 0.06), md: 'rgba(255,255,255,0.08)' },
          },
        }}
      >
        <ArrowBack sx={{ fontSize: 16 }} />
        anoorehr.com
      </Link>

      {/* Left — Branded Panel */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          px: 6,
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: 'absolute', top: -80, right: -80,
            width: 300, height: 300, borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.04)',
          }}
        />
        <Box
          sx={{
            position: 'absolute', bottom: -120, left: -60,
            width: 400, height: 400, borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.03)',
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 420 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <AnoorehrLogo size={56} textColor="common.white" />
          </Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: TYPE.xl, lineHeight: 1.6 }}>
            Unified ERP platform — accounting, payroll, HR, and group consolidation across all subsidiaries.
          </Typography>

          {/* Feature pills */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mt: 4 }}>
            {MARKETING_PILLARS.map((f) => (
              <Box
                key={f}
                sx={{
                  px: 1.5, py: 0.5, borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.15)',
                  bgcolor: 'rgba(255,255,255,0.06)',
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: TYPE.base, fontWeight: TYPE.medium,
                }}
              >
                {f}
              </Box>
            ))}
          </Box>
        </Box>

        <Typography
          sx={{ position: 'absolute', bottom: 24, color: 'rgba(255,255,255,0.3)', fontSize: TYPE.base }}
        >
          &copy; {new Date().getFullYear()} {t('app.name')}
        </Typography>
      </Box>

      {/* Right — Login Form */}
      <Box
        sx={{
          flex: { xs: 1, md: '0 0 480px' },
          display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          px: { xs: 3, sm: 6 }, py: 4,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          {/* Logo */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ mb: 3 }}>
              <AnoorehrLogo size={40} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: TYPE.bold, color: 'text.primary' }}>
              {step === '2fa'
                ? t('auth.verify_identity', 'Verify your identity')
                : t('auth.login', 'Sign in')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {step === '2fa'
                ? t('auth.enter_2fa_code', 'Enter the 6-digit code from your authenticator app')
                : t('auth.login_subtitle', 'Enter your credentials to access the platform')}
            </Typography>
          </Box>

          {/* ── Error / Lockout Alert — always-mounted so layout doesn't shift ── */}
          <Box sx={{ minHeight: 56, mb: error ? 1 : 0 }}>
            {error && (
              <Alert
                severity="error"
                action={
                  lockout && lockoutRemaining > 0 ? (
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: TYPE.bold, color: 'error.main', whiteSpace: 'nowrap', pr: 1, pt: 0.5 }}
                    >
                      {formatCountdown(lockoutRemaining)}
                    </Typography>
                  ) : undefined
                }
              >
                {error}
                {lockout && (
                  <Box sx={{ mt: 0.5 }}>
                    <Link
                      href={`mailto:${EMAIL.support}`}
                      sx={{ fontSize: TYPE.base, fontWeight: TYPE.medium }}
                    >
                      {t('auth.contact_support', 'Contact support')}
                    </Link>
                  </Box>
                )}
              </Alert>
            )}
          </Box>

          {/* ══════════════════════════════════════════════════ */}
          {/* STEP 1: CREDENTIALS                               */}
          {/* ══════════════════════════════════════════════════ */}
          {step === 'credentials' && (
            <Box component="form" onSubmit={handleLogin}>
              {/* Demo account picker — requires BOTH a dev build AND the
                  server's ENVIRONMENT != production. The server flag is the
                  authoritative gate; the build-mode check is belt-and-braces
                  to keep the dropdown invisible on any prod bundle. */}
              {import.meta.env.DEV && demoServerEnabled === true && (
                <Box
                  component="select"
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    const acct = DEMO_ACCOUNTS.find((a) => a.email === e.target.value);
                    if (acct) {
                      setEmail(acct.email);
                      setPassword(DEMO_PASSWORD);
                      setShowPassword(true);
                      setFieldErrors({});
                      setPwKey((k) => k + 1);
                    }
                    e.target.value = '';
                  }}
                  defaultValue=""
                  sx={{
                    width: '100%',
                    mb: 2,
                    p: 1,
                    borderRadius: 1,
                    border: '1px dashed',
                    borderColor: 'warning.main',
                    bgcolor: 'warning.50',
                    fontSize: TYPE.base,
                    color: 'text.secondary',
                    cursor: 'pointer',
                    '&:focus': { outline: 'none', borderColor: 'primary.main' },
                  }}
                >
                  <option value="" disabled>Quick Login — select a demo account...</option>
                  {DEMO_ACCOUNTS.map((acct) => (
                    <option key={acct.email} value={acct.email}>
                      {acct.label} — {acct.email}
                    </option>
                  ))}
                </Box>
              )}

              {/* Email */}
              <Typography variant="caption" sx={{ fontWeight: TYPE.semibold, color: 'text.primary', mb: 0.5, display: 'block' }}>
                {t('auth.email', 'Email')}
              </Typography>
              <TextField
                fullWidth
                placeholder="you@company.com"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setFieldErrors((p) => ({ ...p, email: undefined }));
                  // BUG-048: the lockout is per-account; clear it (and the countdown
                  // that disables the submit button) when a different email is entered.
                  if (lockout || lockoutRemaining > 0) {
                    setLockout(null);
                    setLockoutRemaining(0);
                    setError(undefined);
                  }
                }}
                required
                autoFocus
                autoComplete="email"
                error={!!fieldErrors.email}
                helperText={fieldErrors.email || ' '}
                sx={{ mb: 1.5 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              {/* Password */}
              <Typography variant="caption" sx={{ fontWeight: TYPE.semibold, color: 'text.primary', mb: 0.5, display: 'block' }}>
                {t('auth.password', 'Password')}
              </Typography>
              <TextField
                key={pwKey}
                fullWidth
                placeholder={t('auth.enter_password', 'Enter your password')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setFieldErrors((p) => ({ ...p, password: undefined })); }}
                onKeyDown={handleKeyEvent}
                onKeyUp={handleKeyEvent}
                required
                autoComplete="off"
                error={!!fieldErrors.password}
                helperText={fieldErrors.password || ' '}
                sx={{ mb: 0 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small" tabIndex={-1}>
                          {showPassword
                            ? <VisibilityOff sx={{ fontSize: 18, color: 'text.secondary' }} />
                            : <Visibility sx={{ fontSize: 18, color: 'text.secondary' }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              {/* Caps Lock warning — fixed height so it never shifts the rows below */}
              <Box sx={{ height: 24, display: 'flex', alignItems: 'center', mb: 0.5 }}>
                {capsLock && (
                  <>
                    <KeyboardCapslock sx={{ fontSize: 14, color: 'warning.main', mr: 0.5 }} />
                    <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: TYPE.medium }}>
                      {t('auth.caps_lock_on', 'Caps Lock is on')}
                    </Typography>
                  </>
                )}
              </Box>

              {/* Forgot password */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Box
                  component="a"
                  href="/forgot-password"
                  onClick={(e: React.MouseEvent) => { e.preventDefault(); navigate('/forgot-password'); }}
                  sx={{
                    color: 'primary.main', fontSize: TYPE.base, fontWeight: TYPE.semibold,
                    textDecoration: 'none', '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('auth.forgot_password', 'Forgot password?')}
                </Box>
              </Box>

              {/* Submit */}
              <Button
                type="submit" fullWidth variant="contained" disabled={loading || lockoutRemaining > 0}
                sx={{
                  py: 1.3, fontSize: TYPE.lg, fontWeight: TYPE.semibold,
                  textTransform: 'none', borderRadius: 2,
                  bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                {loading
                  ? <CircularProgress size={22} sx={{ color: 'primary.contrastText' }} />
                  : t('auth.login', 'Sign in')}
              </Button>

              {/* WTH — Auth0 CIAM SSO button (gated). Hidden until founder
                  flips VITE_AUTH0_ENABLED=true on this environment. */}
              {import.meta.env.VITE_AUTH0_ENABLED === 'true' && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ my: 2 }}>or</Divider>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      const base = import.meta.env.VITE_API_BASE_URL || '';
                      window.location.href = `${base}/auth/auth0/login?product_code=anoorehr`;
                    }}
                    data-testid="login-with-auth0"
                    sx={{ py: 1.3, fontSize: TYPE.lg, fontWeight: TYPE.semibold, textTransform: 'none', borderRadius: 2 }}
                  >
                    Continue with Auth0
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* ══════════════════════════════════════════════════ */}
          {/* STEP 2: 2FA VERIFICATION                          */}
          {/* ══════════════════════════════════════════════════ */}
          {step === '2fa' && (
            <Box component="form" onSubmit={handleVerify2FA}>
              {twoFaUser && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
                  {t('auth.2fa_sent_to', 'Signing in as')} <strong>{twoFaUser.email}</strong>
                </Typography>
              )}

              <Typography variant="caption" sx={{ fontWeight: TYPE.semibold, color: 'text.primary', mb: 0.5, display: 'block' }}>
                {recoveryMode
                  ? t('auth.recovery_code', 'Recovery code')
                  : t('auth.authenticator_code', 'Authenticator code')}
              </Typography>
              <TextField
                fullWidth
                placeholder={recoveryMode ? 'XXXX-XXXX-XXXX' : '000000'}
                value={totpCode}
                onChange={(e) => {
                  const v = recoveryMode
                    ? e.target.value.toUpperCase().slice(0, 16)
                    : e.target.value.replace(/\D/g, '').slice(0, 6);
                  setTotpCode(v);
                  setFieldErrors((p) => ({ ...p, totp: undefined }));
                }}
                autoFocus
                autoComplete="one-time-code"
                inputMode={recoveryMode ? 'text' : 'numeric'}
                error={!!fieldErrors.totp}
                helperText={fieldErrors.totp}
                sx={{ mb: 1.5 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    sx: recoveryMode
                      ? { letterSpacing: '0.15em', fontWeight: TYPE.semibold, fontFamily: 'monospace' }
                      : { letterSpacing: '0.3em', fontWeight: TYPE.bold, fontSize: TYPE.xl },
                  },
                }}
              />

              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setRecoveryMode(m => !m);
                  setTotpCode('');
                  setFieldErrors({});
                }}
                sx={{
                  mb: 2, textTransform: 'none', color: 'text.secondary',
                  fontSize: TYPE.sm, fontWeight: TYPE.medium, px: 0,
                }}
              >
                {recoveryMode
                  ? t('auth.use_authenticator', 'Use authenticator code instead')
                  : t('auth.use_recovery_code', "Can't access your authenticator? Use a recovery code")}
              </Button>

              <Button
                type="submit" fullWidth variant="contained" disabled={loading}
                sx={{
                  py: 1.3, fontSize: TYPE.lg, fontWeight: TYPE.semibold,
                  textTransform: 'none', borderRadius: 2,
                  bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                {loading
                  ? <CircularProgress size={22} sx={{ color: 'primary.contrastText' }} />
                  : t('auth.verify', 'Verify')}
              </Button>

              <Button
                fullWidth variant="text" onClick={handleBack}
                startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
                sx={{
                  mt: 1.5, textTransform: 'none', color: 'text.secondary',
                  fontSize: TYPE.base, fontWeight: TYPE.medium,
                }}
              >
                {t('auth.back_to_login', 'Back to sign in')}
              </Button>
            </Box>
          )}

          {/* ── User Guide ─────────────────────────────────── */}
          <Button
            href={SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
            variant="outlined"
            sx={{
              mt: 3, py: 1, fontSize: TYPE.base, fontWeight: TYPE.medium,
              textTransform: 'none', borderRadius: 2, borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'primary.main', color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            User Guide
          </Button>

          {/* ── Signup link ────────────────────────────────── */}
          <Typography variant="body2" sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
            Don&apos;t have an account?{' '}
            <Box
              component="a" href="/signup?plan=free"
              onClick={(e: React.MouseEvent) => { e.preventDefault(); navigate('/signup?plan=free'); }}
              sx={{
                color: 'primary.main', fontWeight: TYPE.semibold,
                textDecoration: 'none', '&:hover': { textDecoration: 'underline' },
              }}
            >
              Start free trial
            </Box>
          </Typography>

          {/* ── Need help? ─────────────────────────────────── */}
          <Box
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 0.5, mt: 2,
            }}
          >
            <HelpOutlineRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {t('auth.need_help', 'Need help?')}{' '}
              <Link
                href={`mailto:${EMAIL.support}`}
                sx={{ color: 'text.secondary', fontWeight: TYPE.medium, fontSize: 'inherit' }}
              >
                {EMAIL.support}
              </Link>
            </Typography>
          </Box>

          {/* ── Footer ─────────────────────────────────────── */}
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', mt: 1.5, color: 'text.disabled', fontSize: TYPE.base }}
          >
            Secured with 256-bit encryption &middot; v0.1.0
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
